import { ApolloError, useApolloClient, useMutation, useQuery } from "@apollo/client";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog, ResourceInfoDisplay } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { WasteClassification, WasteStream } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { ProvisionalWasteStreamChip } from "../../ProvisionalWasteStreamChip";
import { useWasteFacilityState } from "../../WasteFacilityContext";
import { MergedDestinationsDataGrid } from "./MergedDestinationsDataGrid";
import { MergedWasteStreamClassificationInput } from "./MergedWasteStreamClassificationInput";
import { MergedWasteStreamCommentInput } from "./MergedWasteStreamCommentInput";
import { MergedWasteStreamInfo } from "./MergedWasteStreamInfo";
import { MergedWasteStreamInternalNotesInput } from "./MergedWasteStreamInternalNotesInput";
import { MergedWasteStreamNameInput } from "./MergedWasteStreamNameInput";
import { MergedWasteStreamsDataGrid } from "./MergedWasteStreamsDataGrid";
import { UnmergeContainerReattribution } from "./UnmergeContainerReattribution";

type WasteStreamUnmergeFormData = {
  comment?: string | null;
  internalNote?: string | null;
  wasteStreamName: string;
  wasteStreamId: string;
  classification?: WasteClassification;
};

type WasteStreamUnmergeDialogProps = {
  onClose: () => void;
  /**
   * `registration` is omitted as well as `facility`: callers pass a query result
   * that selects only a few of the linked registration's fields, which doesn't
   * satisfy the full nested type. Nothing here reads either one.
   */
  wasteStream: Omit<WasteStream, "facility" | "registration">;
};

const ORIGINAL_WASTE_STREAMS = gql(`
  query OriginalWasteStreams($wasteStreamId: ID!, $startDate: Date, $endDate: Date, $view: FacilityWasteView, $tags: [String!] ) {
    originalWasteStreamsById(wasteStreamId: $wasteStreamId, startDate: $startDate, endDate: $endDate, view: $view, tags: $tags) {
      items {
        id
        name
        vendorProfileNumber
        eManifestProfileNumber
        federalWasteCodes
        stateWasteCodes
        classification
        formCode
        sourceCode
        vendorAmount
        eManifestAmount
        vendorAmountAllTime
        eManifestAmountAllTime
      }
      count
    }
  }
`);

const UNMERGE_WASTE_STREAMS = gql(`
  mutation UnmergeWasteStreams($wasteStreamId: ID!, $containerAssignments: [ContainerReattributionInput!]) {
    unmergeWasteStreams(input:  {
       wasteStreamId: $wasteStreamId
       containerAssignments: $containerAssignments
    })
  }
`);

const UNMERGE_WASTE_STREAM_CONTAINERS = gql(`
  query UnmergeWasteStreamContainers($wasteStreamIds: [ID!]!) {
    wasteStreamContainers(wasteStreamIds: $wasteStreamIds) {
      id
      containerLabel
    }
  }
`);

const EDIT_WASTE_STREAM = gql(`
  mutation EditWasteStream($id: ID!, $name: String, $classification: WasteClassification, $comment: String, $internalNote: String) {
    updateWasteStream(input:  {
       id: $id
       name: $name
       classification: $classification
       comment: $comment
       internalNote: $internalNote
    })
  }
`);

export const WasteStreamUnmergeDialog = ({
  onClose,
  wasteStream,
}: WasteStreamUnmergeDialogProps) => {
  const alerts = useAlerts();
  const navigate = useNavigate();
  const client = useApolloClient();
  const {
    wasteFacilityState: { deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();

  const defaultValues: WasteStreamUnmergeFormData = useMemo(
    () => ({
      comment: wasteStream.comment,
      internalNote: wasteStream.internalNote,
      wasteStreamName: wasteStream.name,
      wasteStreamId: wasteStream.id,
      classification: wasteStream.classification ?? undefined,
    }),
    [wasteStream]
  );

  const methods = useForm<WasteStreamUnmergeFormData>({
    defaultValues,
  });

  const { data, loading } = useQuery(ORIGINAL_WASTE_STREAMS, {
    variables: {
      wasteStreamId: wasteStream.id,
      startDate: deferredFilters.startDate,
      endDate: deferredFilters.endDate,
      view: deferredFilters.view,
      tags: deferredFilters.tags,
    },
  });

  const [unmerge, { loading: unmerging }] = useMutation(UNMERGE_WASTE_STREAMS);

  // The re-attribution picker is only surfaced when the tenant has container
  // tracking enabled; the query is flag-gated server-side, so skip it when the
  // flag is off (otherwise it would be forbidden).
  const { featureFlags } = useFeatureFlags();
  const containerTrackingEnabled = !!featureFlags[FeatureFlagKey.ContainerTracking];

  const { data: containerData, loading: containersLoading } = useQuery(
    UNMERGE_WASTE_STREAM_CONTAINERS,
    {
      variables: { wasteStreamIds: [wasteStream.id] },
      skip: !containerTrackingEnabled || !wasteStream.id,
      // Reopen must reflect current containers, not a stale cache from an
      // earlier open — the picker's reattribution acts on this list.
      fetchPolicy: "cache-and-network",
    }
  );
  const containers = useMemo(() => containerData?.wasteStreamContainers ?? [], [containerData]);

  // The edit mutation returns no fields, so Apollo can't auto-update the
  // normalized cache. Refetch the detail query (which drives this dialog's
  // values) and the grid list so saved edits — including the state
  // registration fields — show without a manual reload.
  const [edit, { loading: editing }] = useMutation(EDIT_WASTE_STREAM, {
    refetchQueries: ["GetWasteStream", "WasteStreams"],
  });

  const originalWasteStreams = useMemo(() => data?.originalWasteStreamsById?.items ?? [], [data]);

  const originalWasteStreamNames = useMemo(
    () => originalWasteStreams.map((ws) => ws.name),
    [originalWasteStreams]
  );

  // containerId → chosen original-raw id. Managed as an override map; the
  // effective assignment defaults to the first split stream (originalWasteStreams
  // are ordered, and the first is the legacy follow-the-first destination).
  const firstStreamId = originalWasteStreams[0]?.id;
  const [assignmentOverrides, setAssignmentOverrides] = useState<Record<string, string>>({});
  const effectiveAssignments = useMemo(() => {
    const result: Record<string, string> = {};
    if (!firstStreamId) return result;
    for (const container of containers) {
      result[container.id] = assignmentOverrides[container.id] ?? firstStreamId;
    }
    return result;
  }, [containers, assignmentOverrides, firstStreamId]);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = methods;
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleSave = () => {
    handleSubmit(async (data) => {
      try {
        await edit({
          variables: {
            id: data.wasteStreamId,
            name: data.wasteStreamName,
            classification: data.classification,
            comment: data.comment,
            internalNote: data.internalNote,
          },
        });
        alerts.success("Successfully edited waste stream");
      } catch (err) {
        alerts.error("An error occurred editing the waste stream", err);
        console.error(err);
      }
      onClose();
    })();
  };

  const handleUnmergeClick = () => {
    setShowConfirmDialog(true);
  };

  const handleCancelUnmerge = () => {
    setShowConfirmDialog(false);
  };

  const handleConfirmUnmerge = async () => {
    setShowConfirmDialog(false);
    try {
      await unmerge({
        variables: {
          wasteStreamId: wasteStream.id,
          containerAssignments: containers.map((container) => ({
            containerId: container.id,
            originalWasteStreamRawId: effectiveAssignments[container.id],
          })),
        },
      });
      alerts.success("Successfully unmerged");
      // Refetch only the WasteStreams query for the current facility.
      // See comment in WasteStreamMergeDialog for details on why we
      // use client.refetchQueries with onQueryUpdated.
      await client.refetchQueries({
        include: ["WasteStreams"],
        onQueryUpdated(observableQuery) {
          if (observableQuery.variables?.facilityId === effectiveWasteFacilityId) {
            return observableQuery.refetch();
          }
          return false;
        },
      });
    } catch (err) {
      console.error(err);
      const message =
        err instanceof ApolloError
          ? err.graphQLErrors.map((e) => e.message).join("; ") || err.message
          : err instanceof Error
            ? err.message
            : "An unexpected error occurred";
      alerts.error("There was an error unmerging this wastestream", message);
    }
    navigate(-1);
  };

  const saving = useMemo(() => unmerging || editing, [unmerging, editing]);

  const dateRangeText = useMemo(() => {
    const startDate =
      deferredFilters.startDate && getUtcDisplayDate(deferredFilters.startDate, "short");
    const endDate = deferredFilters.endDate && getUtcDisplayDate(deferredFilters.endDate, "short");
    if (!startDate || !endDate) return "";
    return `${startDate}-${endDate}`;
  }, [deferredFilters.startDate, deferredFilters.endDate]);

  return (
    <FormProvider {...methods}>
      <Dialog
        open={true}
        onClose={onClose}
        title="Edit merged waste stream"
        maxWidth="lg"
        fullWidth
        noPrimaryAction={true}
        secondaryActionElements={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
        ]}
        additionalPrimaryActionElements={[
          <Button
            key="unmerge"
            variant="contained"
            // Gate on both queries so a container assignment can't be confirmed
            // against a half-loaded picker (empty split streams or containers).
            disabled={saving || loading || containersLoading}
            onClick={handleUnmergeClick}
            startIcon={<CallSplitIcon />}
          >
            Unmerge Waste Streams
          </Button>,
          <Button
            key="save"
            variant="contained"
            disabled={saving || !isDirty}
            onClick={handleSave}
            startIcon={<SaveIcon />}
            sx={{
              mr: 1,
            }}
          >
            Save
          </Button>,
        ]}
      >
        <Box>
          {wasteStream.isProvisional && (
            <Box sx={{ mb: 2 }}>
              <ProvisionalWasteStreamChip isProvisional />
            </Box>
          )}
          {/* Helper text */}
          <Typography sx={{ mb: 3, color: "text.secondary" }}>
            View original waste streams, merged data, destinations/management methods. Unmerge if
            needed.
          </Typography>

          {/* Original waste streams */}
          <Typography sx={{ mb: 3 }}>Original waste streams</Typography>
          <MergedWasteStreamsDataGrid
            wasteStreams={originalWasteStreams as WasteStream[]}
            dateRangeText={dateRangeText}
            loading={loading}
          />

          {/* Merged waste streams section */}
          <Typography sx={{ my: 3 }}>Merged waste streams</Typography>

          {/* Name and classification input */}
          <Grid container spacing={3}>
            <Grid size={6}>
              <MergedWasteStreamNameInput
                control={control}
                wasteStreamNames={originalWasteStreamNames}
              />
            </Grid>
            <Grid size={6}>
              <MergedWasteStreamClassificationInput control={control} />
            </Grid>
          </Grid>

          {/* Info section */}
          <Box sx={{ mt: 3 }}>
            <MergedWasteStreamInfo
              wasteStreams={[wasteStream]}
              dateRangeText={dateRangeText}
              isEditMode={true}
            />
          </Box>

          {/* Comment field */}
          <Box sx={{ mt: 3 }}>
            <MergedWasteStreamCommentInput control={control} />
          </Box>

          {/* Internal Note field */}
          <Box sx={{ mt: 3 }}>
            <MergedWasteStreamInternalNotesInput control={control} />
          </Box>

          {/* Merged Destinations Data Grid */}
          <MergedDestinationsDataGrid wasteStreamIds={wasteStream?.id ? [wasteStream.id] : []} />
        </Box>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        header="Are you sure you want to unmerge these waste streams?"
        open={showConfirmDialog}
        onClose={handleCancelUnmerge}
        onCancel={handleCancelUnmerge}
        onConfirm={handleConfirmUnmerge}
        confirmText="Yes, Unmerge"
        confirmStartIcon={<CallSplitIcon />}
        loading={unmerging}
      >
        <Stack spacing={2}>
          {/* Merged Waste Stream */}
          <Box>
            <Grid container alignItems="center">
              <ResourceInfoDisplay
                resourceType="Merged Waste Stream"
                resourceName={wasteStream.name}
                labelSize={4}
                valueSize={8}
              />
              <ResourceInfoDisplay
                resourceType="Aggregated Amount"
                resourceName={wasteStream.vendorAmount || wasteStream.eManifestAmount}
                labelSize={4}
                valueSize={8}
              />
            </Grid>
          </Box>

          <Divider />

          {/* Original Waste Streams */}
          <Box>
            <Stack spacing={1}>
              {originalWasteStreams.map((ws) => (
                <Grid container key={ws.id} alignItems="center">
                  <ResourceInfoDisplay
                    resourceType="Original Waste Stream"
                    resourceName={ws.name}
                    labelSize={4}
                    valueSize={8}
                  />
                  <ResourceInfoDisplay
                    resourceType="Amount [All-time]"
                    resourceName={ws.vendorAmountAllTime || ws.eManifestAmountAllTime}
                    labelSize={4}
                    valueSize={8}
                  />
                </Grid>
              ))}
            </Stack>
          </Box>

          {/* Per-container re-attribution (only when container tracking is on
              and the stream has containers) */}
          {containerTrackingEnabled && containers.length > 0 && (
            <>
              <Divider />
              <UnmergeContainerReattribution
                containers={containers}
                streamOptions={originalWasteStreams.map((ws) => ({ id: ws.id, name: ws.name }))}
                assignments={effectiveAssignments}
                onChange={(containerId, wasteStreamRawId) =>
                  setAssignmentOverrides((prev) => ({ ...prev, [containerId]: wasteStreamRawId }))
                }
              />
            </>
          )}

          {/* Warning text */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action will separate these waste streams for all-time data and cannot be undone.
            Any changes made to the waste stream name will not be saved.
          </Typography>
        </Stack>
      </ConfirmDialog>
    </FormProvider>
  );
};
